import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

export interface SigningRequestDetails {
  type: string;
  items: { label: string; value: string }[];
}

export interface PendingSigningRequest {
  details: SigningRequestDetails;
  onConfirm: () => void;
  onCancel: () => void;
}

interface SigningConfirmationContextType {
  requestSignConfirmation: (details: SigningRequestDetails) => Promise<boolean>;
  pendingRequest: PendingSigningRequest | null;
}

const SigningConfirmationContext = createContext<SigningConfirmationContextType>({
  requestSignConfirmation: () => Promise.resolve(false),
  pendingRequest: null,
});

export const useSigningConfirmation = () => useContext(SigningConfirmationContext);

interface InternalPending {
  details: SigningRequestDetails;
  resolve: (confirmed: boolean) => void;
}

export const SigningConfirmationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pending, setPending] = useState<InternalPending | null>(null);
  const pendingRef = useRef<InternalPending | null>(null);

  // Cleanup: resolve any pending request as canceled on unmount
  useEffect(() => {
    return () => {
      pendingRef.current?.resolve(false);
      pendingRef.current = null;
    };
  }, []);

  const requestSignConfirmation = useCallback(
    (details: SigningRequestDetails): Promise<boolean> => {
      return new Promise((resolve) => {
        // Cancel any existing pending request to prevent leaked promises
        pendingRef.current?.resolve(false);

        const request = { details, resolve };
        pendingRef.current = request;
        setPending(request);
      });
    },
    [],
  );

  const handleConfirm = useCallback(() => {
    pendingRef.current?.resolve(true);
    pendingRef.current = null;
    setPending(null);
  }, []);

  const handleCancel = useCallback(() => {
    pendingRef.current?.resolve(false);
    pendingRef.current = null;
    setPending(null);
  }, []);

  // Expose pending request with confirm/cancel handlers for WalletsWidget integration
  const pendingRequest: PendingSigningRequest | null = pending
    ? { details: pending.details, onConfirm: handleConfirm, onCancel: handleCancel }
    : null;

  return (
    <SigningConfirmationContext.Provider value={{ requestSignConfirmation, pendingRequest }}>
      {children}
    </SigningConfirmationContext.Provider>
  );
};
